import {
    Box,
    Grid,
    GridItem,
    Heading,
    IconButton,
    Spinner,
    TabPanels,
    Tabs,
    Text,
    useOutsideClick,
    useToast
} from '@chakra-ui/react'
import { Icon } from 'components/atoms'
import { ModalBuild } from 'components/molecules'
import { FileGeneratorDeclaration } from 'components/molecules/FileGeneratorDeclarationPDF'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { useAuthContext } from 'contexts/auth'
import { useContractContext } from 'contexts/contract'
import { ContractMode } from 'contexts/contract/interface'
import { motion } from 'framer-motion'
import { useHideElement } from 'hooks/useHideElement'
import { OperationTypes } from 'interfaces/Operations'
import { ContractResponseUpdateDTO } from 'models/ContractResponseUpdateDTO'
import { FormFileDataProps } from 'pages/Contracts/Components/Forms/FormFileData/interfaces'
import StepsForm from 'pages/Contracts/Components/StepsForm'
import { StepsFormProps } from 'pages/Contracts/Components/StepsForm/interface'
import { FileDeclarationItem } from 'pages/Report/types'
import { useEffect, useRef, useState } from 'react'
import {
    Link as LinkRouter,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom'
import { getDeclarationReports } from 'services/report'
import { generateFile } from 'utils/blob'
import { CancelOrRemoveAction } from './components/CancelOrRemoveAction'
import ContractDetail from './components/ContractDetail'
import { ContractDetailProps } from './components/ContractDetail/interfaces'
import { GenerateHistoricPDF } from './components/GenerateHistoricPDF'
import { HistoricDetail } from './components/HistoricDetail'
import { OperationsActions } from './components/OperationsActions'
import { ReasonRejection } from './components/ReasonRejection'
import QRGenerator from './components/TemplatesPdf/QRCodeGenerator'
import {
    ContentTab,
    TabCustom,
    TabListCustom,
    TabPanelCustom,
    TabTitle
} from './styles'
import { dismountUpdateContract } from './utils/dismountUpdateContract'

interface Operation {
    dataCriacao: string
    mensagemRetorno: string
    numeroContrato: string
    tipoOperacao: OperationTypes
    usuario: string
}

const baseURL = window.location.origin
const TAB_HISTORIC = 1

const Contract = () => {
    const toast = useToast()
    const { id = '' } = useParams()
    const navigate = useNavigate()
    const {
        getContract,
        getContractType,
        setTypeContract,
        typeContract,
        currentContractId,
        setFileData,
        getReasonRefusedContract
    } = useContractContext()
    const [disableAllButton, setDisableAllButton] = useState<boolean>(false)
    const [tabIndex, setTabIndex] = useState(0)
    const { isBackoffice } = useAuthContext()
    const [contractID, setContractID] = useState<string>()
    const [dataById, setDataById] = useState<StepsFormProps>()
    const [btnFlow, setbtnFlow] = useState<[]>()
    const [urlRedirectQRCode, setUrlRedirectQRCode] = useState<string>('')
    const [loading, setloading] = useState(false)
    const [loadingFile, setloadingFile] = useState(false)
    const modalRefEditContract = useRef<ModalBuildHandle>(null)
    const [reasonRefused, setReasonRefused] = useState<Operation>()
    const refTooltip = useRef(null)

    useOutsideClick({
        ref: refTooltip
    })

    const handleModalEditOpen = (): void => {
        modalRefEditContract.current?.onOpen()
        callContractById()
    }

    const selector = '[data-testid="contract-pdf-btn"]'
    useHideElement(selector, tabIndex === 1, tabIndex !== 1)

    const handleModalEditClose = (): void => {
        modalRefEditContract.current?.onClose()
        setFileData({} as FormFileDataProps)
    }

    const handleModalEditCloseAndRefresh = (): void => {
        modalRefEditContract.current?.onClose()
        setFileData({} as FormFileDataProps)
        callContractById()
    }

    const generateURLRedirectQRCode = ({
        hashVersao,
        id
    }: ContractResponseUpdateDTO) => {
        return `${baseURL}/visualizar-contrato-qr-code?hash=${hashVersao}&guid=${id}`
    }

    const [searchParams] = useSearchParams()
    const statusRecused = searchParams.get('typeContract') === 'RECUSADO'

    const callContractById = async () => {
        if (statusRecused) {
            getReasonRefusedContract(id)
                .then(({ data }: any) => setReasonRefused(data))
                .finally(() => setloading(false))
        } else {
            setloading(true)

            getContractType(id).then(({ data }) =>
                setbtnFlow(data.filter((res: any) => res != 'INCLUSAO_ANEXO') as never)
            )
            getContract(id)
                .then(({ data }) => {
                    const URLRedirect = generateURLRedirectQRCode(data)
                    setContractID(data.id)
                    setUrlRedirectQRCode(URLRedirect)
                    // Aqui processamos os dados para ter 'vehicles' e 'vehiclesList'
                    setDataById(dismountUpdateContract(data))
                    return data
                })
                .catch(() => {
                    toast({
                        title: 'Sentimos muito!',
                        description:
                            'Ocorreu um erro, tente novamente mais tarde.',
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true
                    })
                    navigate('/contratos')
                })
                .finally(() => setloading(false))
        }
    }

    const callDeclarationById = async (file: FileDeclarationItem) => {
        setloadingFile(true)
        try {
            const res = await getDeclarationReports(id, file.resumida)
            generateFile('PDF', res)
            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo da declaração foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setloadingFile(false)
        }
    }

    useEffect(() => {
        callContractById()
    }, [])

    const buildUrlArrowLeft = (): string => {
        const FROM_REPORT = 'relatorio'
        const from = searchParams.get('from')

        if (from?.includes(FROM_REPORT)) {
            return `/relatorio`
        }

        return '/contratos'
    }

    return (
        <>
            <Tabs
                isLazy
                data-testid="institution-tabs"
                colorScheme="primary"
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                height="100%"
                onChange={index => setTabIndex(index)}
            >
                <TabListCustom data-testid="contract-tab-list">
                    <ContentTab>
                        <TabCustom data-testid="contract-tab-item-view">
                            {statusRecused ? 'Motivo Recusa' : 'Contrato'}
                        </TabCustom>
                        {!statusRecused && <TabCustom>Histórico</TabCustom>}
                    </ContentTab>
                    {!isBackoffice && tabIndex !== TAB_HISTORIC && (
                        <Box display="flex" flexDirection="row" gap="16px">
                            <CancelOrRemoveAction
                                disableAllButton={disableAllButton}
                                setDisableAllButton={setDisableAllButton}
                                contractStatus={
                                    dataById?.contract?.status || ''
                                }
                                contractId={contractID!}
                            />

                            <OperationsActions
                                operations={btnFlow || []}
                                disableAllButton={disableAllButton}
                                contractStatus={
                                    dataById?.contract?.status || ''
                                }
                                onClickBtn={(operation: string) => {
                                    setTypeContract(operation as ContractMode)
                                    handleModalEditOpen()
                                }}
                            />
                        </Box>
                    )}

                    {tabIndex === TAB_HISTORIC && (
                        <GenerateHistoricPDF contractId={id} />
                    )}
                </TabListCustom>
                <div style={{ display: 'none' }}>
                    <QRGenerator
                        value={urlRedirectQRCode}
                        documentId="canvas-qrcode"
                    />
                </div>

                {!loading ? (
                    <Box
                        flex={1}
                        px={6}
                        bg="#ffffff"
                        maxHeight="91%"
                        minHeight="91%"
                        overflow="scroll"
                        rounded={8}
                    >
                        <TabTitle pt={4} data-testid="institution-tab-title">
                            <Grid px={6} py={4}>
                                <GridItem w="100%">
                                    <Box
                                        display="flex"
                                        gap={2}
                                        alignItems="center"
                                    >
                                        <IconButton
                                            data-testid="institution-tab-back"
                                            as={LinkRouter}
                                            to={buildUrlArrowLeft()}
                                            colorScheme="transparent"
                                            color="inherit"
                                            aria-label="edit"
                                            icon={<Icon name="arrow-left" />}
                                        />
                                        <Heading
                                            data-testid="institution-tab-heading"
                                            size="md"
                                            w="100%"
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="row"
                                            >
                                                <Text
                                                    textTransform="inherit"
                                                    fontWeight="normal"
                                                    marginRight="0.3rem"
                                                >
                                                    {'Contrato:'}
                                                </Text>
                                                <Text textTransform="uppercase">
                                                    {
                                                        dataById?.contract
                                                            .numeroContrato
                                                    }
                                                </Text>
                                            </Box>
                                        </Heading>

                                        {!statusRecused && (
                                            <FileGeneratorDeclaration
                                                callAPI={callDeclarationById}
                                                loading={loadingFile}
                                            />
                                        )}
                                    </Box>
                                </GridItem>
                            </Grid>
                        </TabTitle>
                        <TabPanels data-testid="institution-tab-panels">
                            <TabPanelCustom data-testid="institution-tab-panel-contract">
                                {statusRecused ? (
                                    <ReasonRejection {...reasonRefused!} />
                                ) : (
                                    <ContractDetail
                                        /* CORREÇÃO AQUI: Passar dataById em vez de currentContractId */
                                        contractSelected={
                                            dataById as unknown as ContractDetailProps
                                        }
                                    />
                                )}
                            </TabPanelCustom>
                            {!statusRecused && (
                                <TabPanelCustom>
                                    <HistoricDetail />
                                </TabPanelCustom>
                            )}
                        </TabPanels>
                    </Box>
                ) : (
                    <Box
                        as={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        height="100%"
                        position="relative"
                    >
                        <Spinner
                            size="xl"
                            speed="1s"
                            position="absolute"
                            top="calc(50% - 33px)"
                            left="calc(50% - 33px)"
                        />
                    </Box>
                )}
            </Tabs>

            <ModalBuild
                ref={modalRefEditContract}
                closeOnOverlayClick={false}
                size="5xl"
                shut="white"
                onAfterCloseRefresh={handleModalEditCloseAndRefresh}
            >
                <StepsForm
                    onCancel={handleModalEditClose}
                    onSubmit={handleModalEditCloseAndRefresh}
                    dataToEdit={dataById}
                    type={typeContract}
                />
            </ModalBuild>
        </>
    )
}

export default Contract